import {
  GenerateRangesRequest,
  PeriodStartEndDates,
  ReportColumnTypes,
  ReportConfiguration,
  ReportDataColumn,
  ReportTimeframe,
  ReportTimeframes,
  reportUtil,
} from '@hone-automation/common';
import { findSignificantChanges, normalizeAccountName } from '../../Dashboard/DParser/DPLAnalysis/analysis';
import { AIAnalysisData, AIChartSeries, HierarchicalAccountData } from './aiAnalysis';

interface PeriodAIMetrics extends PeriodMetrics {
  period: string;
  metrics: AIMetrics;
}

type AIMetrics = {
  totalSales: number;
  totalCogs: number;
  totalLabor: number;
  laborMargin: number;
  cogsMargin: number;
  primeCost: number;
  primeCostMargin: number;
};

type HierarchicalDataPair = { dateIndex: number; data: HierarchicalAccountData };

//TODO: Need to test and check these names more - could be different across templates
const ReportSectionMapping: Record<string, any> = {
  'NET SALES': { sales: 'total' },
  'TOTAL COGS': { cogs: 'total' },
  'TOTAL LABOR': { labor: 'total' },
  'TOTAL OPERATING EXPENSES': { operatingExpenses: 'total' },
  'TOTAL CONTROLLABLE EXPENSES': { operatingExpenses: { controllableExpenses: 'total' } },
  'TOTAL NON-CONTROLLABLE EXPENSES': { operatingExpenses: { nonControllableExpenses: 'total' } },
};

const ReportSubSectionMapping: Record<string, any> = {
  SALES: { name: 'sales' },
  'COST OF GOODS SOLD (COGS)': { name: 'cogs' },
  LABOR: { name: 'labor' },
  'CONTROLLABLE EXPENSES': { name: 'controllableExpenses', path: ['operatingExpenses'] },
  'NON-CONTROLLABLE EXPENSES': { name: 'nonControllableExpenses', path: ['operatingExpenses'] },
};

export function generateAIAnalysisDataV2(
  report: NestedHoneReport,
  weekStart?: WeekDay,
  reportCFG?: ReportConfiguration,
  requestedTimeframe?: ReportTimeframe
): AIAnalysisData | null {
  // Generating AI Analysis Data
  const hierarchicalData = hierarchicalDataFromReport(report, weekStart, reportCFG, requestedTimeframe);
  const metrics = calculateAIMetrics(hierarchicalData);
  const statistics = calculateAIStatistics(metrics);
  const significantChanges = findSignificantChanges(metrics);
  const aiChartMetrics = getAIReportChartMetricsV2(hierarchicalData);
  const isPeriod = requestedTimeframe === 'Month' && reportCFG?.data?.periodReporting;
  const timeframe = isPeriod ? 'Fiscal Period' : requestedTimeframe || 'Week';

  return {
    reportId: report.id || '',
    timeframe,
    data: {
      metrics,
      hierarchicalData,
      aiChartMetrics,
    },
    statistics,
    significantChanges,
  };
}

/*  TODO: This is a temporary function to generate chart metrics for the AI Analysis report
    Original was in aiAnalysis.ts and will be removed once we solve the year frontiers issue on the chart headers
    This way , we will use Archier's metrix for charts and we will remove completely the charts from the LLM response
*/

export function getAIReportChartMetricsV2(hierarchicalData: HierarchicalAccountData[]): AIChartSeries[] {
  const chartMetrics: AIChartSeries[] = [
    {
      id: 'netSales',
      name: 'Net Sales',
      data: [],
    },
    {
      id: 'cogs',
      name: 'COGS',
      data: [],
    },
    {
      id: 'labor',
      name: 'Labor',
      data: [],
    },
  ];
  hierarchicalData.forEach(item => {
    chartMetrics[0].data.unshift({ x: item.periodId, y: item.data.sales.total });
    chartMetrics[1].data.unshift({ x: item.periodId, y: item.data.cogs.total });
    chartMetrics[2].data.unshift({ x: item.periodId, y: item.data.labor.total });
  });
  return chartMetrics;
}

function addYearToFirstPeriod(date: PeriodStartEndDates, timeframe: ReportTimeframe): any {
  // Get the period name and dates
  let periodName = date.name || '';

  const startYear = new Date(date.start).getFullYear();
  const endYear = new Date(date.end).getFullYear();

  // Check if we need to add year suffix based on timeframe and year changes
  let shouldAddYear = false;

  switch (timeframe.toLowerCase()) {
    case 'quarter': {
      // In case we receive M01 or P01, we need to extract number from period name (M1, M2, P1, P2, etc.) and calculate quarter
      const periodMatch = periodName.match(/^[MP](\d+)$/i);
      if (periodMatch) {
        const periodNumber = parseInt(periodMatch[1]);
        const quarter = Math.min(Math.floor(periodNumber / 4) + 1, 4);
        // Update the period name to show as quarter
        periodName = `Q${quarter}`;
      }
      if (periodName.match(/^(Q1)$/i)) {
        shouldAddYear = true;
      }
      break;
    }

    case 'month':
      // For months, add year if it's January or if it spans years
      if (periodName.match(/^(M01|P01|M1|P1)$/i)) {
        shouldAddYear = true;
      }
      break;

    case 'week':
      // For weeks, add year if it's Week 1 or if it spans years
      if (periodName.match(/^(W01|W1|Week\s*1)$/i)) {
        shouldAddYear = true;
      }
      break;

    case 'year to date':
      // For yearly reports, always add year
      shouldAddYear = true;
      break;
  }
  // Add year suffix if needed
  if (shouldAddYear) {
    const yearToUse = endYear > startYear ? endYear : startYear;
    const yearSuffix = `'${yearToUse.toString().slice(-2)}`;
    periodName = `${periodName} ${yearSuffix}`;
  }
  return {
    ...date,
    name: periodName,
    periodId: periodName,
  };
}

function hierarchicalDataFromReport(
  report: NestedHoneReport,
  weekStart?: WeekDay,
  reportCFG?: ReportConfiguration,
  requestedTimeframe?: ReportTimeframe
): HierarchicalAccountData[] {
  if (!report.dates?.length) return [];
  const hierarchicalData: HierarchicalDataPair[] = [];
  report.dates.forEach((date, dateIndex) => {
    if (date.type === ReportColumnTypes.Data) {
      if (!date.name) {
        fillName(date, requestedTimeframe ?? getTimeframe(report), weekStart, reportCFG);
      }
      const correctedDate = addYearToFirstPeriod(date, requestedTimeframe || getTimeframe(report));
      hierarchicalData.push({ dateIndex, data: createDefault(correctedDate, report.timeframe) });
    }
  });
  processSections(hierarchicalData, report.sections);

  return hierarchicalData.map(d => d.data).sort((a, b) => a.periodStart.localeCompare(b.periodStart));
}

function getTimeframe(report: NestedHoneReport): ReportTimeframe {
  switch (report.timeframe) {
    case 'Monthly':
      return ReportTimeframes.Month;
    case 'Quarterly':
      return ReportTimeframes.Quarter;
    case 'Weekly':
      return ReportTimeframes.Week;
    case 'Yearly':
    case 'YTD Monthly':
      return ReportTimeframes.Year;
    case 'YTD':
      return ReportTimeframes.Year_to_Date;
    default:
      return ReportTimeframes.Week;
  }
}

function fillName(
  date: PeriodStartEndDates,
  requestedTimeframe: ReportTimeframe,
  weekStart?: WeekDay,
  reportCFG?: ReportConfiguration
) {
  const req = new GenerateRangesRequest(requestedTimeframe, weekStart, reportCFG?.data);
  req.start = date.start;
  req.end = date.end;

  if (requestedTimeframe === 'Year to Date') {
    const month = new Date(date.end).getMonth() + 1;
    const prefix = reportCFG?.data?.periodReporting ? 'P' : 'M';
    date.name = prefix + month.toString().padStart(2, '0');
  } else {
    date.name = reportUtil.generateRanges(req)[0]?.name;
  }
}

function processSections(hierarchicalData: HierarchicalDataPair[], sections?: NestedHoneReportSection[]) {
  sections?.forEach(section => {
    const mapping = ReportSectionMapping[section.title];
    if (mapping) {
      hierarchicalData.forEach(item => {
        const row = section.data[item.dateIndex];
        setFieldValue(row, mapping, item.data.data as any);
      });
    } else {
      const subMapping = ReportSubSectionMapping[section.title];
      if (subMapping) {
        performSubMapping(hierarchicalData, subMapping.path ?? [], section);
      }
    }
    processSections(hierarchicalData, section.sections);
  });
}

function normalizeTitle(title: string) {
  const subMapping = ReportSubSectionMapping[title];
  if (subMapping) {
    return subMapping.name;
  }
  return normalizeAccountName(title);
}

function findOrCreateParentObject(mappingObj: any, sectionName: string) {
  const found = mappingObj[sectionName];
  if (!found) {
    return { accounts: {} };
  }

  if (typeof found === 'object') {
    return found;
  }

  // In this case, found is a not an object so we set it up as a parent object
  return { value: found, accounts: {} };
}

function performSubMapping(
  hierarchicalData: HierarchicalDataPair[],
  mappingPath: string[],
  section: NestedHoneReportSection
) {
  const sectionName = normalizeTitle(section.title);

  if (section.sections.length) {
    hierarchicalData.forEach(item => {
      const mappingObj: any = mappingPath.reduce((acc, cur) => (!acc ? acc : (acc as any)[cur]), item.data.data);
      if (mappingObj) {
        const obj: any = findOrCreateParentObject(mappingObj, sectionName);
        const row = section.data[item.dateIndex];
        if (row && row.amount) {
          obj.value = row.amount;
        }
        mappingObj[sectionName] = obj;
      }
    });
    section.sections.forEach(subSection =>
      performSubMapping(hierarchicalData, [...mappingPath, sectionName, 'accounts'], subSection)
    );
  } else if (mappingPath.length) {
    hierarchicalData.forEach(item => {
      const row = section.data[item.dateIndex];
      const mappingObj: any = mappingPath.reduce((acc, cur) => (!acc ? acc : (acc as any)[cur]), item.data.data);
      if (row && mappingObj) {
        // Check for a total and attempt to apply to a sibling section (if one can be found)
        if (sectionName.startsWith('total_')) {
          const totalOf = sectionName.replace('total_', '');
          const totalOfObj = mappingObj[totalOf];
          if (totalOfObj && typeof totalOfObj === 'object') {
            const obj: any = {};
            obj[totalOf] = 'total';
            setFieldValue(row, obj, mappingObj);
          } else if (!totalOfObj && totalOfObj !== 0) {
            setFieldValue(row, sectionName, mappingObj);
          }
          // In the else case totalOfObj is a not an object which means that section being totaled has no children
          // Therefore, we can simply ignore this section since it adds no additional information to the data
        } else {
          setFieldValue(row, sectionName, mappingObj);
        }
      }
    });
  }
  // else this is a case where a top-level item (sales, cogs, labor) has no children -
  // this should be ignored and handled via totals instead
}

function setFieldValue(row: ReportDataColumn, mapping: any, value: any) {
  if (!value || !row) {
    return;
  }

  if (typeof mapping === 'string') {
    // Check if value is an object before trying to set a property on it
    if (typeof value !== 'object' || value === null) {
      console.warn(`Cannot set property ${mapping} on non-object value:`, value);
      return;
    }
    value[mapping] = row.amount;
  } else if (typeof mapping === 'object') {
    const [k, v] = Object.entries(mapping)[0];
    // Check if value[k] exists and is an object before recursing
    if (typeof value !== 'object' || value === null) {
      console.warn(`Cannot access property ${k} on non-object value:`, value);
      return;
    }
    // Initialize the nested object if it doesn't exist
    if (value[k] === undefined) {
      value[k] = {};
    }
    setFieldValue(row, v, value[k]);
  } // Else - do nothing
}

function createDefault(dates: PeriodStartEndDates, periodType: string): HierarchicalAccountData {
  return {
    periodId: dates.name ?? '',
    periodStart: dates.start,
    periodEnd: dates.end,
    periodType, // Can be modified based on actual period type
    data: {
      sales: { total: 0, accounts: {} },
      cogs: { total: 0, accounts: {} },
      labor: { total: 0, accounts: {} },
      operatingExpenses: {
        total: 0,
        accounts: {},
        controllableExpenses: { total: 0, accounts: {} },
        nonControllableExpenses: { total: 0, accounts: {} },
      },
    },
  } as HierarchicalAccountData;
}

function calculateAIMetrics(hierarchicalData: HierarchicalAccountData[]): PeriodAIMetrics[] {
  return hierarchicalData.map(item => ({
    period: item.periodId,
    metrics: {
      totalSales: item.data.sales.total,
      totalCogs: item.data.cogs.total,
      totalLabor: item.data.labor.total,
      laborMargin: (item.data.labor.total / item.data.sales.total) * 100,
      cogsMargin: (item.data.cogs.total / item.data.sales.total) * 100,
      primeCost: item.data.cogs.total + item.data.labor.total,
      primeCostMargin: ((item.data.cogs.total + item.data.labor.total) / item.data.sales.total) * 100,
    },
  }));
}

function calculateAIStatistics(metrics: PeriodAIMetrics[]) {
  // Get the most recent and previous periods
  const current = metrics[metrics.length - 1];
  const previous = metrics[metrics.length - 2];

  return {
    sales: calcStats(current, previous, 'totalSales', metrics),
    cogs: calcStats(current, previous, 'totalCogs', metrics),
    labor: calcStats(current, previous, 'totalLabor', metrics),
    primeCost: calcStats(current, previous, 'primeCost', metrics),
    margins: {
      laborMargin: calcStats(current, previous, 'laborMargin', metrics),
      cogsMargin: calcStats(current, previous, 'cogsMargin', metrics),
      primeCostMargin: calcStats(current, previous, 'primeCostMargin', metrics),
    },
  };
}

function calcStats(
  c: PeriodAIMetrics,
  p: PeriodAIMetrics | null,
  metricName: keyof AIMetrics,
  metrics: PeriodAIMetrics[]
) {
  return {
    currentValue: c.metrics[metricName],
    previousValue: p?.metrics[metricName],
    changeAmount: p ? c.metrics[metricName] - p.metrics[metricName] : null,
    changePercent: calculateChangePercent(c, p, metricName),
    average: calculateAverage(metricName, metrics),
  };
}

function calculateChangePercent(c: PeriodAIMetrics, p: PeriodAIMetrics | null, metricName: keyof AIMetrics) {
  if (!p) {
    return null;
  }

  const changeAmount = c.metrics[metricName] - p.metrics[metricName];

  // Special case: No change (or both are 0), return 0 (avoids possible div by 0)
  if (changeAmount === 0) {
    return 0;
  }
  // Special case: previous is 0 (but current is not), return null to indicate undefined / infinite
  if (p.metrics[metricName] === 0) {
    return null;
  }

  // In this case the division should be "safe"
  return (changeAmount / p.metrics[metricName]) * 100;
}

function calculateAverage(metricName: keyof AIMetrics, metrics: PeriodAIMetrics[]) {
  // First filter out any entries where metrics[metricName] is null
  const values: number[] = metrics.map(m => m.metrics[metricName]).filter((v): v is number => v !== null);

  if (values.length === 0) return 0;

  // Now TypeScript knows this is an array of numbers, not null
  const sum = values.reduce((acc, val) => acc + val, 0);
  return sum / values.length;
}
